import { ActivityType } from '../service/ActivityFeedStorageItem';
import type { OutOfRoutineNotificationActivityFeedItem } from '../service/ActivityFeedItem';
import {
    activityFeedOutOfRoutineNotificationTitle,
    activityFeedOutOfRoutineNotificationOrganiserHeroActionText,
    activityFeedOutOfRoutineNotificationHeroActionText,
} from './activityFeedCard.locstring.json';
import type {
    OpenCalendarMeetingActionType,
    MakeApiCallActionType,
    ActivityFeedActionType,
} from '../components/ActivityFeedActions';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import {
    formatDayAtTime,
    formatMonthDay,
    formatUserTime,
    formatWeekDay,
    parse,
} from 'owa-datetime';
import type { OutOfRoutinePayload } from '../service/OutOfRoutinePayload';
import { ClientItemId, getUserMailboxInfo } from 'owa-client-ids';
import { isPastTime } from 'owa-observable-datetime';
import {
    ActivityFeedCardDetails,
    ActivityCardLayoutType,
    formatBoldMarkdownString,
    HeroActionProperties,
} from './feedCardPresentationDetails';

export function getOutOfRoutineFeedCard(
    item: OutOfRoutineNotificationActivityFeedItem
): ActivityFeedCardDetails {
    let payload: OutOfRoutinePayload;
    if (item.payload) {
        payload = item.payload as OutOfRoutinePayload;
    }

    let meetingOrganizer = payload.Inference.Observations.filter(
        observation => observation.Name == 'MeetingOrganizerEmail'
    );

    if (payload) {
        let subject = payload.Inference.Subject.Value;
        let cardTitle = loc(activityFeedOutOfRoutineNotificationTitle);
        let meetingStartTimeValue = payload.Inference.Observations.filter(
            observation => observation.Name == 'MeetingStartTime'
        );

        let senderInfo = {};
        if (meetingOrganizer) {
            senderInfo = [{ name: '', emailAddress: meetingOrganizer[0].Value }];
        }
        let meetingId: String;
        let meetingItemId: ClientItemId;
        if (payload.Suggestion.SuggestedActions) {
            meetingId = payload.Suggestion.SuggestedActions[0].MeetingItemId;
            meetingItemId = {
                Id: meetingId.toString(),
                mailboxInfo: getUserMailboxInfo(),
            };
        }
        let meetingTime = '';
        if (meetingStartTimeValue) {
            // As we get the time value in users timezone we dont need to convert it in another timezone, hence passing UTC
            let meetingStartDateTime = parse('UTC', meetingStartTimeValue[0].Value.toString());
            meetingTime = formatDayAtTime(
                formatWeekDay(meetingStartDateTime) + ', ' + formatMonthDay(meetingStartDateTime),
                formatUserTime(meetingStartDateTime)
            );
            // We dont want to show the card for past events
            if (isPastTime(meetingStartDateTime)) {
                return {} as ActivityFeedCardDetails;
            }
        }
        const insightsFeedbackUrl = `https://outlook.office.com/weveb2/api/v1.0/me/insights('${item.payload.Inference.Id}')/record`;
        let suggestedActionName = '';
        if (item.payload.Suggestion.SuggestedActions) {
            suggestedActionName = item.payload.Suggestion.SuggestedActions[0].Name.toString();
        }
        cardTitle = item.isSeen ? cardTitle : formatBoldMarkdownString(cardTitle);
        let isOrganiser = getIsOrganiser(meetingOrganizer ? meetingOrganizer[0].Value : null);
        const heroActionTitle = getActionTitle(isOrganiser);
        let extraInformation =
            (isOrganiser ? 'OorOrganizer' : 'OorRecipient') + ',MeetingId:' + meetingId;
        const cardAction = getActionForCard(
            meetingItemId,
            extraInformation,
            insightsFeedbackUrl,
            suggestedActionName
        );
        return {
            senderInfo: senderInfo,
            internetMessageId: meetingId,
            cardTitle: cardTitle,
            timeStamp: item.timestamp,
            isSeen: item.isSeen,
            cardType: ActivityCardLayoutType.MultiTitleActivityCard,
            activityType: ActivityType.OutOfRoutineNotificationInsight,
            cardIcon: ControlIcons.Calendar,
            cardOnClickAction: cardAction,
            cardSubTitle1: meetingTime,
            cardSubTitle2: subject,
            heroButtonProperties: [
                {
                    buttonTitle: heroActionTitle,
                } as HeroActionProperties,
            ],
        } as ActivityFeedCardDetails;
    }
    return {} as ActivityFeedCardDetails;
}

function getActionForCard(
    meetingItemId: ClientItemId,
    extraInformation: string,
    insightsFeedbackUrl: string,
    suggestedActionName: string
): ActivityFeedActionType[] {
    return [
        {
            actionName: 'Action.OpenCalendarEvent',
            meetingItemId: meetingItemId,
        } as OpenCalendarMeetingActionType,
        {
            actionName: 'Action.MakeApiCall',
            url: insightsFeedbackUrl,
            body: JSON.stringify({
                action: {
                    Name: suggestedActionName,
                },
            }),
            actionNameExtraInformation: extraInformation,
            methodType: 'POST',
        } as MakeApiCallActionType,
    ];
}

function getIsOrganiser(organiser: String): boolean {
    let currUser = getUserMailboxInfo();
    return (
        currUser.userIdentity &&
        organiser?.toLocaleLowerCase() === currUser.userIdentity.toLocaleLowerCase()
    );
}

function getActionTitle(isOrganiser: boolean) {
    return isOrganiser
        ? loc(activityFeedOutOfRoutineNotificationOrganiserHeroActionText)
        : loc(activityFeedOutOfRoutineNotificationHeroActionText);
}
